import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";

// Libreria
import { Accordion, Card, Icon, Button, Dropdown, Form } from "semantic-ui-react";

// Lodash
import { cloneDeep, get, map, uniqueId, slice, trim } from "lodash";

// Components
import ConfirmModal from "@Modals/ConfirmModal";
import TextAreaInput from "@Components/TextArea";
import Input from "@Components/Input";

// StaticData
import { TYPE_OPTIONS, ACQ_OPTIONS } from "./staticData";
import styles from "./styles.module.css";

const FieldsEditor = ({ value, onChange, errors = {}, readOnly = false, modalOption }) => {
	const controlled = Array.isArray(value) && typeof onChange === "function";
	const [internal, setInternal] = useState([]);
	const [showAll, setShowAll] = useState(false);

	// Delete States
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [deleteIdx, setDeleteIdx] = useState(null);

	const list = useMemo(() => {
		return controlled ? value : internal;
	}, [controlled, value, internal]);
	const apply = controlled ? onChange : setInternal;

	const getErr = useCallback(
		(i, k) => {
			const id = get(list, [i, "id"]);
			return get(errors, [id, k]) ?? get(errors, [i, k]) ?? null;
		},
		[errors, list]
	);

	const nextOrder = useCallback(() => {
		return (
			list.reduce((m, f) => {
				const n = Number(get(f, "order"));
				return Number.isFinite(n) && n > m ? n : m;
			}, 0) + 1
		);
	}, [list]);

	const updateField = useCallback(
		(idx, key, val) => {
			const copy = cloneDeep(list);
			if (!copy[idx]) return;
			copy[idx][key] = val;
			if (key === "acquisition") {
				copy[idx].acquisition = val;
				delete copy[idx].value;
				if (val !== "input") copy[idx].question = "";
			}
			if (key === "type" && val !== "dropdown") {
				delete copy[idx].options;
			}
			apply(copy);
		},
		[apply, list]
	);

	const handleToggleAll = useCallback(() => {
		if (!list.length) return;
		const next = !showAll;
		const updated = map(list, (f) => {
			return { ...f, active: next };
		});
		apply(updated);
		setShowAll(next);
	}, [apply, list, showAll]);

	const handleToggleOne = useCallback(
		(_e, data) => {
			const idx = data.index;
			const copy = cloneDeep(list);
			if (!copy[idx]) return;
			copy[idx].active = !copy[idx].active;
			apply(copy);
		},
		[apply, list]
	);

	// Abre el modal de confirmación y guarda el índice a eliminar
	const handleRemove = useCallback(
		(e, { fieldIndex }) => {
			e.stopPropagation();
			if (readOnly) return;
			const idx = Number(fieldIndex);
			const item = list?.[idx];
			if (!item) return;

			// Si no tiene nombre, eliminar directo
			if (!trim(item.name)) {
				apply([...slice(list, 0, idx), ...slice(list, idx + 1)]);
				return;
			}
			setDeleteIdx(idx);
			setShowDeleteConfirm(true);
		},
		[apply, list, readOnly]
	);

	// Cierra el modal y limpia el índice
	const dismissRemove = useCallback(() => {
		setShowDeleteConfirm(false);
		setDeleteIdx(null);
	}, []);

	// Confirma la eliminación, aplica el cambio y cierra el modal
	const confirmRemove = useCallback(() => {
		if (deleteIdx === null) {
			dismissRemove();
			return;
		}
		apply(
			list.filter((_, i) => {
				return i !== deleteIdx;
			})
		);
		dismissRemove();
	}, [apply, list, deleteIdx, dismissRemove]);

	const handleAdd = useCallback(() => {
		apply([
			...list,
			{
				id: uniqueId("fld_"),
				type: "string",
				name: "",
				label: "",
				order: nextOrder(),
				acquisition: "static",
				question: "",
				active: true,
			},
		]);
	}, [apply, list, nextOrder]);

	const handleNumberChange = useCallback(
		(_e, data) => {
			const idx = Number(get(data, "fieldIndex"));
			const val = data.value === "" ? "" : Number(data.value);
			updateField(idx, data.name, val);
		},
		[updateField]
	);

	const handleTextChange = useCallback(
		(_e, data) => {
			const idx = Number(get(data, "fieldIndex"));
			updateField(idx, data.name, data.value);
		},
		[updateField]
	);

	const handleDropdownChange = useCallback(
		(_e, data) => {
			const idx = Number(get(data, "fieldIndex"));
			updateField(idx, data.name, data.value);
		},
		[updateField]
	);

	const handleQuestionChangeAt = useCallback(
		(idx) => {
			return (e, data) => {
				const val = data?.value ?? e?.target?.value ?? "";
				updateField(idx, "question", val);
			};
		},
		[updateField]
	);

	const isNameRequired = (f) => {
		return f?.acquisition === "static";
	};

	const parseDropdownOptions = (inputValue) => {
		if (!inputValue || inputValue.trim() === "") return [];

		// Si contiene saltos de línea, usar split por líneas
		if (inputValue.includes("\n")) {
			return inputValue
				.split("\n")
				.map((option) => {
					return option.trim();
				})
				.filter((option) => {
					return option !== "";
				});
		}

		// Si contiene comas, usar split por comas
		if (inputValue.includes(",")) {
			return inputValue
				.split(",")
				.map((option) => {
					return option.trim();
				})
				.filter((option) => {
					return option !== "";
				});
		}

		// Si es una sola línea, tratarla como una opción
		return [inputValue.trim()].filter((option) => {
			return option !== "";
		});
	};

	const handleOptionsChange = useCallback(
		(idx) => {
			return (e) => {
				const val = e?.target?.value ?? "";
				const copy = cloneDeep(list);
				if (copy[idx]) {
					copy[idx].inputOptionsRaw = val;
					const options = parseDropdownOptions(val);
					copy[idx].inputOptions = options;
					copy[idx].options = options;
					apply(copy);
				}
			};
		},
		[apply, list]
	);

	const getCompactIssues = useCallback(
		(i, field) => {
			const issues = [];
			if (getErr(i, "order")) issues.push("Orden esta vacio");
			if (isNameRequired(field) && getErr(i, "name")) issues.push("Nombre esta vacio");
			if (getErr(i, "label")) issues.push("Label esta vacio");
			if (getErr(i, "type")) issues.push("Tipo esta vacio");
			if (getErr(i, "acquisition")) issues.push("Acquisition esta vacio");
			if (field?.type === "dropdown" && (getErr(i, "inputOptions") || getErr(i, "options"))) {
				issues.push("Opciones esta vacio");
			}
			// if (field?.acquisition === "input" && getErr(i, "question")) issues.push("Pregunta esta vacio");
			return issues;
		},
		[getErr]
	);

	return (
		<Accordion fluid>
			<fieldset className={styles.card}>
				<legend className={styles.legendS}>
					<span>Lista de Campos</span>
					<div
						className={`${styles.flexEnd} ${!list.length ? styles.disabled : ""}`}
						role="button"
						tabIndex={!list.length ? -1 : 0}
						onClick={!list.length ? undefined : handleToggleAll}>
						<Icon disabled={!list.length} name={showAll ? "chevron down" : "chevron up"} className={styles.chevronIconPre} />
						<span disabled={!list.length} className={styles.textToggle}>
							{showAll ? "Colapsar Todas" : "Expandir Todas"}
						</span>
					</div>
				</legend>

				{list.map((field, i) => {
					const eOrder = getErr(i, "order");
					const eLabel = getErr(i, "label");
					const nameRequired = isNameRequired(field);
					const eNameRaw = getErr(i, "name");
					const eName = nameRequired ? eNameRaw : null;
					const eType = getErr(i, "type");
					const eAcq = getErr(i, "acquisition");
					const eInputOptions = getErr(i, "inputOptions");
					// const eValue = getErr(i, "value");
					// const eQuestion = getErr(i, "question");
					const issues = getCompactIssues(i, field);

					return (
						<Card key={field?.id ?? i} className={styles.fieldset}>
							<Accordion.Title active={!!field?.active} index={i} onClick={handleToggleOne} className={styles.accordionTitle}>
								<div className={styles.divTitle}>
									<Icon name="trash" onClick={handleRemove} fieldIndex={i} className={styles.removeItemButton} disabled={readOnly} />
									<div className={styles.titleLeft}>
										<Icon name="dropdown" />
										{i + 1}. {field?.label || "(sin nombre)"}
									</div>

									{!field?.active && issues.length > 0 && (
										<div className={styles.miniIssues} aria-label="Errores del field">
											{map(issues, (t) => {
												return (
													<span key={t} className={styles.badge}>
														{t}
													</span>
												);
											})}
										</div>
									)}
								</div>
							</Accordion.Title>

							<Accordion.Content active={!!field?.active}>
								<Form noValidate>
									<div className={styles.containerSecond}>
										<Form.Field required error={!!eOrder} className={styles.inputGroupBO}>
											<span className={styles.label}>
												Orden:<span className={styles.required}> *</span>
											</span>
											<Input
												type="number"
												min={0}
												step="1"
												value={field?.order ?? ""}
												name="order"
												fieldIndex={i}
												onChange={handleNumberChange}
												disabled={readOnly || modalOption === "Add"}
												fluid
												className={styles.inputOrderClass}
											/>
											{eOrder && <div className="ui pointing red basic label">{eOrder}</div>}
										</Form.Field>

										<div className={styles.subContainerFirts}>
											<Form.Field required error={!!eType} className={styles.inputGroupBT}>
												<span className={styles.label}>
													Tipo:<span className={styles.required}> *</span>
												</span>
												<Dropdown
													selection
													options={TYPE_OPTIONS}
													value={field?.type ?? "string"}
													name="type"
													fieldIndex={i}
													onChange={handleDropdownChange}
													disabled={readOnly}
													fluid
												/>
												{eType && <div className="ui pointing red basic label">{eType}</div>}
											</Form.Field>

											<Form.Field required error={!!eAcq} className={styles.inputGroupBA}>
												<span className={styles.label}>
													Adquisición :<span className={styles.required}> *</span>
												</span>
												<Dropdown
													selection
													options={ACQ_OPTIONS}
													value={field?.acquisition ?? "generated"}
													name="acquisition"
													fieldIndex={i}
													onChange={handleDropdownChange}
													disabled={readOnly}
													className={styles.classAdq}
													fluid
												/>
												{eAcq && <div className="ui pointing red basic label">{eAcq}</div>}
											</Form.Field>
										</div>
									</div>

									<div className={styles.container}>
										<Form.Field error={!!eLabel} className={styles.inputGroupB}>
											<span className={styles.label}>
												Label:<span className={styles.required}> *</span>
											</span>
											<Input
												value={field?.label ?? ""}
												name="label"
												fieldIndex={i}
												onChange={handleTextChange}
												disabled={readOnly}
												className={styles.inputClass}
											/>
											{eLabel && <div className="ui pointing red basic label">{eLabel}</div>}
										</Form.Field>

										<Form.Field error={!!eName} className={styles.inputGroupB}>
											<span className={styles.label}>
												Nombre:
												{isNameRequired(field) && <span className={styles.required}> *</span>}
											</span>

											<Input
												value={field?.name ?? ""}
												name="name"
												fieldIndex={i}
												onChange={handleTextChange}
												disabled={readOnly}
												fluid
												className={styles.inputClass}
											/>
											{eName && <div className={`${styles.errorMessage} ui pointing red basic label`}>{eName}</div>}
										</Form.Field>
									</div>

									{field?.type === "dropdown" && (
										<Form.Field className={styles.inputGroupBQ}>
											<TextAreaInput
												label="Opciones:"
												showRequired
												name="inputOptions"
												value={field?.inputOptionsRaw ?? field?.inputOptions?.join("\n") ?? field?.options?.join("\n") ?? ""}
												placeholder="Ingresa las opciones con salto de línea o comas"
												onChange={handleOptionsChange(i)}
												disabled={readOnly}
												minRows={1}
												className={styles.inputField}
											/>
											{eInputOptions && <div className={`${styles.errorMessage} ui pointing red basic label`}>{eInputOptions}</div>}
										</Form.Field>
									)}
									{/* {field?.acquisition === "input" && ( */}
									<Form.Field className={styles.inputGroupBQ}>
										<TextAreaInput
											label="Texto:"
											name="question"
											value={String(field?.question || "")}
											placeholder="Escribe la pregunta que se hará al usuario"
											onChange={handleQuestionChangeAt(i)}
											fieldIndex={i}
											disabled={readOnly}
											minRows={1}
											maxRows={6}
											showCount
											maxLength={200}
											className={styles.inputField}
										/>
										{/* {eQuestion && <div className="ui pointing red basic label">{eQuestion}</div>} */}
									</Form.Field>
									{/* )} */}
								</Form>
							</Accordion.Content>
						</Card>
					);
				})}

				<Button type="button" onClick={handleAdd} className={styles.butttonAdd} disabled={readOnly}>
					Agregar Campo
				</Button>
				{showDeleteConfirm && (
					<ConfirmModal
						buttonLabel="Eliminar"
						visible
						header="Confirmar Eliminar"
						fullContent="¿Está seguro que desea eliminar este campo? Esta acción no se puede deshacer."
						onConfirm={confirmRemove}
						onDismiss={dismissRemove}
					/>
				)}
			</fieldset>
		</Accordion>
	);
};

FieldsEditor.propTypes = {
	value: PropTypes.array,
	onChange: PropTypes.func,
	errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
	readOnly: PropTypes.bool,
	modalOption: PropTypes.string,
};

export default FieldsEditor;
